import { useMemo } from 'react';
import { buildMarkdownBlocks } from '@org/utils-client';
import { cn } from '../../../../utils';
import { ContentDescription } from '../shared/content-description';
import { useLessonLinkClasses } from '../shared/lesson-link-classes';
import {
  isLikelyMarkdownContent,
  renderInlineMarkdown,
  renderMarkdownBlocks,
  type MarkdownRenderClassNames,
} from '../../../custom/markdown';
import type { ContentDisplayVariant } from '../../hooks/content-renderer-types';
import type { StudentTextContent } from '../../hooks/use-class-blocks';
import './text-content.css';

interface TextContentDisplayProps {
  content: StudentTextContent;
  variant?: ContentDisplayVariant;
}

export function TextContentDisplay({ content, variant = 'card' }: TextContentDisplayProps) {
  const isExplicitMarkdown = content.format === 'markdown';
  const shouldRenderMarkdownBody = isExplicitMarkdown || isLikelyMarkdownContent(content.text);
  const markdownBlocks = useMemo(
    () => (shouldRenderMarkdownBody ? buildMarkdownBlocks(content.text ?? '') : []),
    [shouldRenderMarkdownBody, content.text]
  );
  const canRenderMarkdownBody = shouldRenderMarkdownBody && markdownBlocks.length > 0;
  const linkClasses = useLessonLinkClasses();
  const inlineClasses = useMemo<MarkdownRenderClassNames>(
    () => ({ ...baseInlineClasses, ...linkClasses }),
    [linkClasses]
  );
  const blockClasses = useMemo<MarkdownRenderClassNames>(
    () => ({ ...baseBlockClasses, ...linkClasses }),
    [linkClasses]
  );

  return (
    <div
      className={cn(
        'space-y-2',
        variant === 'card' && 'rounded-lg border border-border bg-card p-4 shadow-sm'
      )}
    >
      {content.title && (
        <h3 className="text-lg font-semibold">
          {renderInlineMarkdown(content.title, 'text-content-title', inlineClasses)}
        </h3>
      )}
      <ContentDescription text={content.description} markdown />
      <div
        className={cn(
          'text-content-body text-foreground',
          canRenderMarkdownBody ? 'text-content-body--markdown' : 'whitespace-pre-wrap'
        )}
      >
        {canRenderMarkdownBody
          ? renderMarkdownBlocks(markdownBlocks, 'text-content-markdown', blockClasses)
          : renderInlineMarkdown(content.text, 'text-content', inlineClasses)}
      </div>
    </div>
  );
}
const baseInlineClasses: MarkdownRenderClassNames = {
  strong: 'text-content-strong',
  italic: 'text-content-italic',
  underline: 'text-content-underline',
  // color: intentionally undefined - let Tailwind color classes apply directly
};

const baseBlockClasses: MarkdownRenderClassNames = {
  ...baseInlineClasses,
  paragraph: 'text-content-paragraph',
  heading: 'text-content-heading',
  blockquote: 'text-content-blockquote',
  blockquoteLine: 'text-content-blockquote-line',
  blockquoteGap: 'text-content-blockquote-gap',
};
